import React from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { ToastProvider } from './components/ui/Toast';
import { Header } from './components/Header';
import { MobileNav } from './components/MobileNav';
import { StudentDashboard } from './components/student/StudentDashboard';
import { QuizRevisionView } from './components/student/QuizRevisionView';
import { QuizTestView } from './components/student/QuizTestView';
import { ScorecardView } from './components/student/ScorecardView';
import { AnalyticsView } from './components/student/AnalyticsView';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { QuestionBankManager } from './components/admin/QuestionBankManager';
import { QuestionSetManager } from './components/admin/QuestionSetManager';
import { SectorSubjectManager } from './components/admin/SectorSubjectManager';
import { AttemptsManager } from './components/admin/AttemptsManager';
import { UserManager } from './components/admin/UserManager';
import { AuthModal } from './components/auth/AuthModal';
import { ProfileModal } from './components/auth/ProfileModal';

import { LogIn } from 'lucide-react';

const AppContent: React.FC = () => {
  const { currentUser, currentView, setAuthModalOpen } = useApp();

  return (
    <div className="min-h-screen bg-[#F7FAFA] text-[#111111] flex flex-col font-sans antialiased selection:bg-[#E8FAF9] selection:text-[#111111]">
      <Header />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pt-5 sm:pt-6">
        {!currentUser ? (
          <div className="min-h-[55vh] flex items-center justify-center py-12 px-4">
            <div className="max-w-md w-full text-center space-y-5 bg-white rounded-3xl p-8 border border-[#E5E9E9] shadow-lg">
              <div className="w-14 h-14 rounded-2xl bg-[#E8FAF9] text-[#1fb4ac] mx-auto flex items-center justify-center border border-[#56E8E1]/40 overflow-hidden p-2">
                <img src="/launch_icon.webp" alt="REVise" className="w-full h-full object-contain" />
              </div>
              <div>
                <h2 className="text-xl font-extrabold text-[#111111] mb-2">Sign In Required</h2>
                <p className="text-xs text-[#5F6368] leading-relaxed">
                  Please sign in with your account to access your revision workspace, practice question sets, and progress analytics.
                </p>
              </div>
              <button
                onClick={() => setAuthModalOpen(true)}
                className="w-full py-3 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] font-bold text-xs rounded-xl shadow-xs transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <LogIn className="w-4 h-4" />
                Sign In / Register
              </button>
            </div>
          </div>
        ) : (
          <>
            {currentUser.role === 'student' && (
              <>
                {currentView === 'student-dashboard' && <StudentDashboard />}
                {currentView === 'quiz-revision' && <QuizRevisionView />}
                {currentView === 'quiz-test' && <QuizTestView />}
                {currentView === 'quiz-scorecard' && <ScorecardView />}
                {currentView === 'student-analytics' && <AnalyticsView />}
                {!['student-dashboard', 'quiz-revision', 'quiz-test', 'quiz-scorecard', 'student-analytics'].includes(currentView) && (
                  <StudentDashboard />
                )}
              </>
            )}

            {currentUser.role === 'admin' && (
              <>
                {currentView === 'admin-dashboard' && <AdminDashboard />}
                {currentView === 'admin-users' && <UserManager />}
                {currentView === 'admin-questions' && <QuestionBankManager />}
                {currentView === 'admin-sets' && <QuestionSetManager />}
                {(currentView === 'admin-sectors' || currentView === 'admin-subjects') && (
                  <SectorSubjectManager />
                )}
                {currentView === 'admin-attempts' && <AttemptsManager />}
                {!['admin-dashboard', 'admin-users', 'admin-questions', 'admin-sets', 'admin-sectors', 'admin-subjects', 'admin-attempts'].includes(currentView) && (
                  <AdminDashboard />
                )}
              </>
            )}
          </>
        )}
      </main>

      {/* Persistent subtle footer */}
      <footer className="border-t border-[#E5E9E9] bg-white/90 backdrop-blur-xs py-6 text-center text-xs text-[#5F6368] mb-14 md:mb-0">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="font-extrabold text-[#111111]">Revise</span>
            <span className="text-[#E5E9E9]">|</span>
            <span className="text-[#5F6368]">Exam Revision & Practice Platform</span>
          </div>
          <div className="flex items-center gap-3 text-[#5F6368]/80 font-medium">
            <span>Instant Feedback</span>
            <span>•</span>
            <span>Timed Exams</span>
            <span>•</span>
            <span>Progress Analytics</span>
          </div>
        </div>
      </footer>

      {/* Responsive bottom mobile navigation */}
      <MobileNav />

      {/* User Authentication & Profile Modals */}
      <AuthModal />
      <ProfileModal />
    </div>
  );
};

export function App() {
  return (
    <AppProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </AppProvider>
  );
}

export default App;
